import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Scoped CSS injected once ─────────────────────────────────────────────── */
const LOGIN_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Manrope:wght@400;500;600;700;800&display=swap');

  .al-split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 100vh;
  }

  /* LEFT */
  .al-left {
    background: #071628;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 52px 56px;
    position: relative;
    overflow: hidden;
  }
  .al-left::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(201,168,76,.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(201,168,76,.04) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
  }
  .al-left::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #C9A84C, transparent);
  }

  .al-left-top  { position: relative; z-index: 1; }
  .al-left-bot  { position: relative; z-index: 1; }

  .al-brand-mark {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 72px;
  }
  .al-op-badge {
    width: 48px; height: 48px;
    border-radius: 50%;
    background: #fff;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Manrope', sans-serif;
    font-weight: 900; font-size: 1rem;
    color: #071628;
    letter-spacing: -.02em;
    flex-shrink: 0;
  }
  .al-brand-name {
    font-family: 'Manrope', sans-serif;
    font-weight: 800; font-size: 1.15rem;
    color: #fff;
    letter-spacing: .01em;
  }
  .al-headline {
    font-family: 'Manrope', sans-serif;
    font-weight: 800;
    font-size: clamp(2.4rem, 3.5vw, 3.6rem);
    line-height: 1.02;
    letter-spacing: -.04em;
    color: #fff;
    margin-bottom: 28px;
    max-width: 12ch;
  }
  .al-headline em { font-style: normal; color: #C9A84C; }
  .al-tagline {
    font-size: .88rem; font-weight: 500;
    color: #8A99A8;
    line-height: 1.65;
    max-width: 36ch;
    border-left: 2px solid #C9A84C;
    padding-left: 16px;
  }

  .al-sectors {
    display: flex; flex-wrap: wrap; gap: 8px;
    margin-bottom: 40px;
  }
  .al-chip {
    font-size: .7rem; font-weight: 700;
    letter-spacing: .1em; text-transform: uppercase;
    color: rgba(201,168,76,.7);
    border: 1px solid rgba(201,168,76,.2);
    border-radius: 4px;
    padding: 5px 10px;
  }
  .al-security {
    display: flex; align-items: center; gap: 10px;
    padding: 14px 18px;
    background: rgba(255,255,255,.04);
    border: 1px solid #1E3450;
    border-radius: 8px;
  }
  .al-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #22c55e; flex-shrink: 0;
    box-shadow: 0 0 8px rgba(34,197,94,.6);
    animation: al-pulse 2s infinite;
  }
  @keyframes al-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: .4; }
  }
  .al-sec-text { font-size: .75rem; color: #8A99A8; line-height: 1.4; }
  .al-sec-text strong { color: rgba(255,255,255,.7); font-weight: 600; }

  /* RIGHT */
  .al-right {
    background: #F5F0E8;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 52px 64px;
    position: relative;
  }
  .al-box { width: 100%; max-width: 380px; }

  .al-eyebrow {
    font-size: .72rem; font-weight: 800;
    letter-spacing: .14em; text-transform: uppercase;
    color: #1B6B72; margin-bottom: 10px;
  }
  .al-title {
    font-family: 'Manrope', sans-serif;
    font-weight: 800; font-size: 1.9rem;
    letter-spacing: -.03em; color: #0D2137;
    margin-bottom: 8px; line-height: 1.1;
  }
  .al-sub {
    font-size: .875rem; color: #5A6472;
    margin-bottom: 40px; line-height: 1.5;
  }

  .al-field { margin-bottom: 20px; }
  .al-field label {
    display: block;
    font-size: .78rem; font-weight: 700;
    letter-spacing: .06em; text-transform: uppercase;
    color: #0D2137; margin-bottom: 8px;
  }
  .al-field input {
    width: 100%; height: 50px;
    padding: 0 16px;
    border: 1.5px solid #D4CEC5;
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: .95rem; color: #0D2137;
    background: #fff;
    outline: none;
    transition: border-color .2s;
    letter-spacing: .04em;
  }
  .al-field input:focus   { border-color: #1B6B72; }
  .al-field input::placeholder { color: #B0AAA0; }

  .al-btn {
    width: 100%; height: 52px;
    background: #0D2137; color: #fff;
    border: none; border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: .9rem; font-weight: 800;
    letter-spacing: .08em; text-transform: uppercase;
    cursor: pointer; margin-top: 8px;
    transition: background .2s;
  }
  .al-btn:hover:not(:disabled) { background: #1B6B72; }
  .al-btn:disabled { opacity: .5; cursor: not-allowed; }

  .al-warning {
    margin-top: 28px; padding: 14px 16px;
    background: rgba(13,33,55,.06);
    border-left: 3px solid #0D2137;
    border-radius: 0 6px 6px 0;
  }
  .al-warning p { font-size: .75rem; color: #4A5568; line-height: 1.55; }
  .al-warning p strong { color: #0D2137; font-weight: 700; }

  .al-footer {
    position: absolute; bottom: 28px;
    font-size: .72rem; color: #A09890;
    text-align: center;
  }

  @media (max-width: 768px) {
    .al-split  { grid-template-columns: 1fr; }
    .al-left   { display: none; }
    .al-right  { padding: 40px 28px; min-height: 100vh; }
  }
`;

export const LoginPage = () => {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) navigate('/admin/dashboard');
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: LOGIN_CSS }} />

      <div className="al-split" data-testid="login-page">

        {/* LEFT BRAND PANEL */}
        <div className="al-left">
          <div className="al-left-top">
            <div className="al-brand-mark">
              <div className="al-op-badge" data-testid="login-logo">OP</div>
              <span className="al-brand-name">Opportunity Post</span>
            </div>
            <h1 className="al-headline">Secure<br />Command<br /><em>Center.</em></h1>
            <p className="al-tagline">Where merit is recognized. Fairness is expected. Opportunity finds its audience.</p>
          </div>

          <div className="al-left-bot">
            <div className="al-sectors">
              {['Education','Healthcare','Business & Finance','Government','Technology & AI','Workforce & Labor'].map(s => (
                <span key={s} className="al-chip">{s}</span>
              ))}
            </div>
            <div className="al-security">
              <div className="al-dot" />
              <p className="al-sec-text"><strong>Secure connection established.</strong> All access attempts are monitored, logged, and reported.</p>
            </div>
          </div>
        </div>

        {/* RIGHT LOGIN PANEL */}
        <div className="al-right">
          <div className="al-box">
            <p className="al-eyebrow">Restricted Access</p>
            <h2 className="al-title" data-testid="login-title">Administrative<br />Authentication</h2>
            <p className="al-sub">Authorized personnel only. Enter your operator credentials to continue.</p>

            <form onSubmit={handleLogin} data-testid="login-form">
              <div className="al-field">
                <label htmlFor="email">Operator ID</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter operator ID"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="al-field">
                <label htmlFor="password">Access Code</label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter access code"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
              </div>

              <button
                type="submit"
                className="al-btn"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Authenticating...' : 'Authenticate →'}
              </button>
            </form>

            <div className="al-warning">
              <p><strong>Warning:</strong> This system is monitored. Unauthorized access attempts are logged, traced, and reported to the appropriate authorities. By proceeding you confirm authorization.</p>
            </div>
          </div>

          <p className="al-footer">© 2026 Opportunity Post · Confidential System Access</p>
        </div>

      </div>
    </>
  );
};
